import { useState, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { callMsGraph, getProfilePhoto } from "../../store/graph";
import "../../assets/css/Header/Header.css";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../store/config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/free-solid-svg-icons";
import { faEnvelope } from '@fortawesome/free-solid-svg-icons'

function Header({ showProfile, toggleSidebar }) {
  const [open, setOpen] = useState(false);
  const profileRef = useRef(null);
  const toggleRef = useRef(null);
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const navigate = useNavigate();

  // GOOGLE USER
  const googleUser = JSON.parse(localStorage.getItem("google_user"));

  // Microsoft profile
  const [profile, setProfile] = useState(null);
  const [photo, setPhoto] = useState(null);

  // ✅ Backend profile
  const [backendProfile, setBackendProfile] = useState(null);

  // Determine login type
  const isGoogleUser = !!googleUser;
  const isMsUser = !!account;


  const logout = async () => {
    const googleUser = JSON.parse(localStorage.getItem("google_user"));

    // Clear app storage
    localStorage.clear();
    sessionStorage.clear();

    // Google logout
    if (googleUser?.access_token) {
      try {
        await fetch(
          `https://oauth2.googleapis.com/revoke?token=${googleUser.access_token}`,
          { method: "POST" },
        );
        window.google?.accounts.id.disableAutoSelect();
      } catch (e) {
        console.error("Google revoke failed", e);
      }
    }

    // Microsoft logout
    if (isMsUser) {
      instance
        .logoutPopup({ postLogoutRedirectUri: "/" })
        .then(() => {
          navigate("/"); // Force redirect after logout
        })
        .catch((err) => console.error("MS logout failed", err));
      return;
    }

    // Default logout redirect
    navigate("/");
  };

  // ---------------- BACKEND PROFILE FETCH ----------------
  useEffect(() => {
    const fetchBackendProfile = async () => {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      try {
        const res = await fetch(
          `${BASE_URL}api/user/profile`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!res.ok) return;

        const data = await res.json();
        setBackendProfile(data);
      } catch (err) {
        console.error("Failed to load backend profile", err);
      }
    };

    fetchBackendProfile();
  }, []);

  // ---------------- MICROSOFT PROFILE ----------------
  useEffect(() => {
    const fetchMsProfile = async () => {
      if (!showProfile || !isMsUser) return;

      try {
        const response = await instance.acquireTokenSilent({
          scopes: ["User.Read"],
          account,
        });

        const profileData = await callMsGraph(response.accessToken);
        setProfile(profileData);
        localStorage.setItem("userProfile", JSON.stringify(profileData));

        try {
          const photoUrl = await getProfilePhoto(response.accessToken);
          setPhoto(photoUrl);
        } catch {
          setPhoto(null);
        }
      } catch (err) {
        console.error("Graph error", err);
      }
    };

    fetchMsProfile();
  }, [account, instance, showProfile, isMsUser]);

  // ---------------- DISPLAY DATA (PRIORITY LOGIC) ----------------
  const displayName = backendProfile
    ? `${backendProfile.first_name || ""} ${backendProfile.last_name || ""}`.trim()
    : isGoogleUser
      ? googleUser.name
      : profile?.displayName || account?.name || "John Doe";

  const displayEmail =
    backendProfile?.email ||
    googleUser?.email ||
    profile?.mail ||
    profile?.userPrincipalName ||
    "—";

  const displayJob =
    backendProfile?.designation ||
    (isGoogleUser ? "-" : profile?.jobTitle) ||
    "-";

  const displayLocation =
    backendProfile?.organization_name ||
    (isGoogleUser ? "—" : profile?.officeLocation) ||
    "—";

  const displayPhoto = backendProfile?.profile_image
    ? `${BASE_URL}/${backendProfile.profile_image}`
    : (photo ?? googleUser?.picture ?? "/assets/images/default_user_image.png");

  // ---------------- CLICK OUTSIDE ----------------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (photo) {
        URL.revokeObjectURL(photo);
      }
    };
  }, [photo]);

  return (
    <>
      <header className="header">
        <div className="header-left">

          {/* ✅ HAMBURGER BUTTON */}
          {toggleSidebar && (
            <button
              className="menu-toggle"
              aria-label="Toggle sidebar"
              onClick={toggleSidebar}
            >
              <FontAwesomeIcon icon={faBars} style={{ color: "white" }} />
            </button>
          )}

          <a href="https://www.ssdntech.com/" title="SSDN Technologies">
            <img
              src="/assets/images/image.png"
              alt="SSDN Logo"
              className="header-logo"
            />
          </a>
        </div>

        <nav className="nav-links">
          <a
            href="https://www.ssdntech.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Home
          </a>
          {/* <a href="#">Services</a> */}
          <a
            href="https://www.ssdntech.com/contact-us"
            target="_blank"
            rel="noopener noreferrer"
          >
            Contact
          </a>

          {showProfile && (
            <button
              ref={toggleRef}
              className="profile-toggle"
              aria-label="Profile"
              onClick={() => setOpen(!open)}
            >
              <img
                src={displayPhoto}
                alt="Profile"
                className="profile-icon"
                referrerPolicy="no-referrer"
              />
            </button>
          )}
        </nav>
      </header>

      {showProfile && (
        <div ref={profileRef} className={`profile-card ${open ? "open" : ""}`}>
          <div className="profile-content">
            <img
              src={displayPhoto}
              alt="Profile"
              className="profile-img"
              referrerPolicy="no-referrer"
            />
            <h3>{displayName}</h3>
            <p className="job">{displayJob}</p>
            <div className="profile-details">
              <p>{displayLocation}</p>
              <p>📧 {displayEmail}</p>
            </div>

            <button
              className="logout-btn"
              onClick={() => {
                setOpen(false);
                navigate("/my-profile");
              }}
            >
              My Profile
            </button>
            <button className="logout-btn" onClick={logout}>
              Logout
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default Header;
